import { useState, useEffect, useRef, Suspense, useCallback } from "react"
import { Canvas, useFrame, useThree } from "@react-three/fiber"
import {
  OrbitControls,
  Environment,
  PerspectiveCamera,
  OrthographicCamera,
  Html,
} from "@react-three/drei"
import * as THREE from "three"
import { ConfigurationControls } from "@/components/configuration-controls"
import { ShelvingModelLoader } from "@/components/shelving-model-loader"
import { FloatingAddSectionButton } from "@/components/floating-add-section-button"
import { Floor } from "@/components/floor"
import { AxisHelper } from "@/components/axis-helper"
import { DimensionLines } from "@/components/dimension-lines"
import { useShelvingStore } from "@/lib/shelving-store"
import { useLanguageStore } from "@/lib/language-store"
import { Button } from "@/components/ui/button"
import { Loader as Loader2, RotateCcw, Box, Square, Axis3d as Axis3D, Eclipse, Layers3, Download, Palette, Ruler } from "lucide-react"
import { ModelErrorBoundary } from "@/components/model-error-boundary"
import { MissingModelsDialog } from "@/components/missing-models-dialog"
import { validateModels } from "@/lib/model-validation"
import { AuthHeader } from "@/components/auth-header"

function ScreenshotHelper({ onScreenshot }: { onScreenshot: (fn: () => void) => void }) {
  const { gl, scene, camera } = useThree()

  useEffect(() => {
    onScreenshot(() => {
      // Render the scene one more time to ensure it's up to date
      gl.render(scene, camera)

      // Get the canvas as data URL
      const dataURL = gl.domElement.toDataURL('image/png')

      // Create a download link
      const link = document.createElement('a')
      link.download = `shelving-configuration-${Date.now()}.png`
      link.href = dataURL
      link.click()
    })
  }, [gl, scene, camera, onScreenshot])

  return null
}

function CanvasInstructions() {
  const { getTranslation } = useLanguageStore()

  return (
    <div className="absolute bottom-2 right-2 z-10 text-[10px] text-gray-600 bg-white/95 px-3 py-2 rounded-xl shadow">
      <p>{getTranslation("canvas.zoom")}</p>
      <p>{getTranslation("canvas.pan")}</p>
      <p>{getTranslation("canvas.rotate")}</p>
    </div>
  )
}

function LoadingSpinner() {
  const { getTranslation } = useLanguageStore()
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 dark:bg-gray-900/80 backdrop-blur-sm z-20 rounded-xl">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-[#212b4f]" />
        <div className="text-center">
          <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
            {getTranslation("loading.models")}
          </p>
        </div>
      </div>
    </div>
  )
}
export default function ShelvingConfigurator() {
  const [isMobile, setIsMobile] = useState(false)
  const [isOrtho, setIsOrtho] = useState(false)
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [showMissingModelsDialog, setShowMissingModelsDialog] = useState(false)
  const [missingModels, setMissingModels] = useState<string[]>([])
  const screenshotFnRef = useRef<(() => void) | null>(null)
  const cameraRef = useRef<THREE.PerspectiveCamera | THREE.OrthographicCamera>(null)
  const controlsRef = useRef<any>(null)
  const { getTranslation } = useLanguageStore()

  const {
    model,
    height,
    width,
    depth,
    sides,
    shelfCount,
    sectionCount,
    frameState,
    showAxisHelper,
    toggleAxisHelper,
    showShadows,
    toggleShadows,
    showFloor,
    toggleFloor,
    gradientBackground,
    toggleGradientBackground,
    showDimensions,
    toggleDimensions,
  } = useShelvingStore()

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  useEffect(() => {
    const checkModels = async () => {
      const result = await validateModels()
      if (!result.allLoaded && result.missingModels.length > 0) {
        setMissingModels(result.missingModels)
        setShowMissingModelsDialog(true)
      }
    }

    const timer = setTimeout(() => {
      checkModels()
    }, 1500)

    return () => clearTimeout(timer)
  }, [])

  // One-time loading state - only runs once when component mounts and models are loading
  useEffect(() => {
    if (isInitialLoading) {
      const timer = setTimeout(() => {
        setIsInitialLoading(false)
        setHasInitiallyLoaded(true)
      }, 2000) // Give models time to load

      return () => clearTimeout(timer)
    }
  }, [isInitialLoading])

  const initialCameraPosition: [number, number, number] = [-1.5, 0.7, 4.0]
  const initialTarget: [number, number, number] = [1, 0.90, 0.5]
  const frontCameraPosition: [number, number, number] = [1.2, 1.0, 6]
  const topCameraPosition: [number, number, number] = [1.2, 8, 0.5]
  const orthoCameraPosition: [number, number, number] = [5, 3, 5]

  const moveCamera = (position: [number, number, number], target: [number, number, number]) => {
    if (cameraRef.current) {
      cameraRef.current.position.set(...position)
      cameraRef.current.lookAt(...target)
      cameraRef.current.updateProjectionMatrix()
    }
  }

  const resetCamera = () => {
    setIsOrtho(false)
    setTimeout(() => {
      moveCamera(initialCameraPosition, initialTarget)
      if (controlsRef.current) {
        controlsRef.current.target.set(...initialTarget)
        controlsRef.current.update()
      }
    }, 50)
  }

const frontView = () => {
    setIsOrtho(true)
    setTimeout(() => {
      moveCamera(frontCameraPosition, initialTarget)
      if (controlsRef.current) {
        controlsRef.current.target.set(...initialTarget)
        controlsRef.current.update()
      }
    }, 50)
  }
  const topView = () => {
    setIsOrtho(true)
    setTimeout(() => {
      moveCamera(topCameraPosition, initialTarget)
      if (controlsRef.current) {
        controlsRef.current.target.set(...initialTarget)
        controlsRef.current.update()
      }
    }, 50)
  }
  const handleScreenshotRegister = useCallback((fn: () => void) => {
    screenshotFnRef.current = fn
  }, [])

  const handleScreenshot = () => {
    if (screenshotFnRef.current) {
      screenshotFnRef.current()
    }
  }

  // Light background style for reset and zoom buttons
  const lightButtonStyle = {
    backgroundColor: '#f8f9fa',
    color: '#212b4f',
    borderColor: '#e9ecef',
    padding: '6px',
    width: '30px',
    height: '30px',
    borderRadius: '10px'
  }

  return (
    <>
      <MissingModelsDialog
        open={showMissingModelsDialog}
        onOpenChange={setShowMissingModelsDialog}
        missingModels={missingModels}
      />
      <div className="flex flex-col md:flex-row gap-3 md:h-screen p-2">
      <div className="relative bg-gray-100 dark:bg-gray-900 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm h-[60vw] min-h-[280px] max-h-[480px] md:h-auto md:max-h-[100dvh] md:flex-1 min-w-0">
        {/* Loading spinner - only shows on first load */}
        {isInitialLoading && <LoadingSpinner />}

        <div className="absolute top-4 right-4 z-30">
          <AuthHeader />
        </div>

        <div className="absolute top-4 left-4 z-30 flex gap-2 flex-wrap">
          <Button
            onClick={toggleGradientBackground}
            variant="outline"
            size="icon"
            style={gradientBackground ? { ...lightButtonStyle, backgroundColor: '#212b4f', color: 'white' } : lightButtonStyle}
            title={getTranslation("toggle.background")}
          >
            <Palette className="h-3.5 w-3.5" />
          </Button>
          <Button
            onClick={toggleFloor}
            variant="outline"
            size="icon"
            style={showFloor ? { ...lightButtonStyle, backgroundColor: '#212b4f', color: 'white' } : lightButtonStyle}
            title={getTranslation("toggle.floor")}
          >
            <Layers3 className="h-3.5 w-3.5" />
          </Button>
          <Button
            onClick={toggleShadows}
            variant="outline"
            size="icon"
            style={showShadows ? { ...lightButtonStyle, backgroundColor: '#212b4f', color: 'white' } : lightButtonStyle}
            title={getTranslation("toggle.shadows")}
          >
            <Eclipse className="h-3.5 w-3.5" />
          </Button>
          {/* Axis helper button hidden - keeping functionality for later
          <Button
            onClick={toggleAxisHelper}
            variant="outline"
            size="icon"
            style={showAxisHelper ? { ...lightButtonStyle, backgroundColor: '#212b4f', color: 'white' } : lightButtonStyle}
            title={getTranslation("toggle.axis")}
          >
            <Axis3D className="h-3.5 w-3.5" />
          </Button>
          */}
          <Button
            onClick={toggleDimensions}
            variant="outline"
            size="icon"
            style={showDimensions ? { ...lightButtonStyle, backgroundColor: '#212b4f', color: 'white' } : lightButtonStyle}
            title={getTranslation("toggle.dimensions")}
          >
            <Ruler className="h-3.5 w-3.5" />
          </Button>
          <Button onClick={handleScreenshot} variant="outline" size="icon" style={lightButtonStyle} title={getTranslation("download.screenshot")}>
            <Download className="h-3.5 w-3.5" />
          </Button>
          <Button onClick={resetCamera} variant="outline" size="icon" style={lightButtonStyle} title={getTranslation("reset.view")}>
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
{/* Hidden for now - keeping functionality for later
          <Button onClick={frontView} variant="outline" size="icon" style={lightButtonStyle} title="Front View (Orthographic)">
            <Square className="h-4 w-4" />
          </Button>
          <Button onClick={topView} variant="outline" size="icon" style={lightButtonStyle} title="Top View (Orthographic)">
            <Box className="h-4 w-4" />
          </Button>
          */}
        </div>

        <ModelErrorBoundary>
          <Canvas
            shadows={showShadows}
            dpr={[1, 2]}
            performance={{ min: 0.5 }}
            gl={{
              antialias: true,
              alpha: true,
              powerPreference: "high-performance",
              stencil: false,
              depth: true
            }}
            frameloop="demand"
            style={{ background: gradientBackground ? 'linear-gradient(to bottom, #e5e7eb 0%, white 100%)' : 'white' }}
          >
            {isOrtho ? (
              <OrthographicCamera makeDefault ref={cameraRef as React.MutableRefObject<THREE.OrthographicCamera>} position={orthoCameraPosition} zoom={50} near={0.1} far={100} />
            ) : (
              <PerspectiveCamera makeDefault ref={cameraRef as React.MutableRefObject<THREE.PerspectiveCamera>} position={initialCameraPosition} fov={45} near={0.1} far={100} />
            )}

            <ambientLight intensity={0.5} />
            <directionalLight
              position={[2, 10, 5]}
              intensity={2.0}
              castShadow={showShadows}
              shadow-mapSize-width={2048}
              shadow-mapSize-height={2048}
              shadow-camera-left={-10}
              shadow-camera-right={10}
              shadow-camera-top={10}
              shadow-camera-bottom={-10}
              shadow-camera-near={0.5}
              shadow-camera-far={50}
              shadow-bias={-0.0001}
            />
            <hemisphereLight
              args={["#ffffff", "#444444", 0.3]}
            />

            <Suspense fallback={null}>
              <ScreenshotHelper onScreenshot={handleScreenshotRegister} />
              <ShelvingModelLoader model={model} height={height} width={width} depth={depth} sides={sides} shelfCount={shelfCount} sectionCount={sectionCount} frameState={frameState} showShadows={showShadows} />
              {showFloor && <Floor position={[15, -0.001, 0]} />}
              <FloatingAddSectionButton />
              <Environment preset="sunset" background={false} />
              <AxisHelper size={0.125} visible={showAxisHelper} />
              <DimensionLines
                visible={showDimensions}
                height={height}
                width={width}
                depth={depth}
                sectionCount={sectionCount}
                sides={sides}
              />
            </Suspense>

            <OrbitControls
              ref={controlsRef}
              makeDefault
              target={initialTarget}
              enableZoom
              minPolarAngle={0}
              maxPolarAngle={Math.PI / 2}
              minDistance={1}
              maxDistance={50}
              enableDamping={false}
              rotateSpeed={0.7}
              zoomSpeed={1.2}
              panSpeed={1.2}
            />
          </Canvas>
        </ModelErrorBoundary>

        <CanvasInstructions />
      </div>

      <div className="flex flex-col overflow-auto w-full md:w-auto md:min-w-[320px] md:max-w-[420px] md:h-full">
        <div className="flex-1 overflow-auto">
          <ConfigurationControls />
        </div>
      </div>
    </div>
    </>
  )
}